import React from 'react'

import SelectPendencyStatus from '../SelectPendencyStatus'
import Chip from 'presentation/shared/components/Chip'
import SupportText from 'presentation/shared/components/SupportText'
import TextField from 'presentation/shared/components/TextField'
import TextArea from 'presentation/shared/components/TextArea'
import Button from 'presentation/shared/components/Button'

import * as S from './styles'
import { DoctorRegistrationSteps } from 'common/enum/doctor-overall-registration-steps'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import { DoctorRegisterStepStatus } from 'domain/entities/doctor-register'
import {
  disableObservation,
  generateNewRegistrationDetailsValidationSchema
} from 'presentation/hospital/layouts/DoctorRegister/NewRegistrationsDetails'
import { newRegistrationDetailsContext } from 'presentation/hospital/layouts/DoctorRegister/NewRegistrationsDetails/context'

type AttendancesProps = {
  attendances?: {
    otherLocationsAttendences?: boolean
    hasPrivateClinic?: boolean
    hasExternalHealthInsurance?: boolean
    otherClinics?: boolean
    otherClinicsName?: string
    externalHealthInsurances?: string[]
    medicalInsurance?: string[]
  }
  initialValues: registerPendencyValues
  updateStepStatus?: (step: any, initialValues: any) => Promise<void>
}

const Attendances = ({
  attendances,
  initialValues,
  updateStepStatus
}: AttendancesProps) => {
  const { isStepsUpdateBlocked } = newRegistrationDetailsContext
  const { handleChange, values, setFieldValue, handleSubmit, isValid } =
    useFormik({
      initialValues: initialValues,
      validateOnMount: true,
      validationSchema: generateNewRegistrationDetailsValidationSchema(
        DoctorRegistrationSteps.ATTENDANCES
      ),
      onSubmit: async (values) => {
        try {
          await updateStepStatus?.(values, initialValues)
          toast.success('Dados enviados com sucesso.')
          window.scrollTo(0, 0)
        } catch (error) {
          toast.error('Erro ao salvar dados.')
        }
      }
    })

  const setPendencyStatus = (field: string, event: any) => {
    setFieldValue(
      `${field}.status`,
      event.target.value === 'Pendente'
        ? DoctorRegisterStepStatus.PENDENT
        : DoctorRegisterStepStatus.APPROVED_DOCTOR_REGISTER
    )
  }

  return (
    <S.Wrapper data-testid="card-attendances">
      <S.PendencySelection>
        <SelectPendencyStatus
          handleChange={(e) =>
            setPendencyStatus(DoctorRegistrationSteps.ATTENDANCES, e)
          }
          initialValue={values[DoctorRegistrationSteps.ATTENDANCES].status}
          data-testid="select-pendency-status-attendances"
          disabled={isStepsUpdateBlocked}
        />
      </S.PendencySelection>
      <S.Title>Atendimentos</S.Title>
      <S.AttendancesContainer>
        <S.InputsContainer>
          <div>
            <SupportText color="primary">
              Presta atendimento em outros hospitais/clínicas?
              <S.Asterisk>*</S.Asterisk>
            </SupportText>
            <S.ChipsContainer>
              {otherLocationsAttendencesOptions.map((attendance, index) => (
                <Chip
                  key={attendance.label}
                  label={attendance.label}
                  customValue={attendance.value}
                  name="otherLocationAttendence"
                  checked={attendance.value === attendances?.otherClinics}
                  data-testid={`check-box-other-location-attendance${index}`}
                />
              ))}
            </S.ChipsContainer>
          </div>
          <div>
            <SupportText color="primary">
              Tem consultório particular?
              <S.Asterisk>*</S.Asterisk>
            </SupportText>
            <S.ChipsContainer>
              {hasPrivateClinicOptions.map((hasPrivateClinic, idx) => (
                <Chip
                  key={hasPrivateClinic.label}
                  label={hasPrivateClinic.label}
                  customValue={hasPrivateClinic.value}
                  name="hasPrivateClinic"
                  checked={
                    hasPrivateClinic.value === attendances?.hasPrivateClinic
                  }
                  data-testid={`check-box-has-private-clinic-${idx}`}
                />
              ))}
            </S.ChipsContainer>
          </div>
          <div>
            <SupportText color="primary">
              Possui convênios externos?
              <S.Asterisk>*</S.Asterisk>
            </SupportText>
            <S.ChipsContainer>
              <Chip
                key={'Sim'}
                label={'Sim'}
                customValue={true}
                name="hasExternalHealthInsurance"
                checked={attendances?.otherClinics}
                data-testid={`check-box-has-external-health-insurance-0`}
              />
              <Chip
                key={'Não'}
                label={'Não'}
                customValue={false}
                name="hasExternalHealthInsurance"
                checked={!attendances?.externalHealthInsurances?.length}
                data-testid={`check-box-has-external-health-insurance-1`}
              />
            </S.ChipsContainer>
          </div>
        </S.InputsContainer>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2,1fr)',
            gridGap: '1rem'
          }}
        >
          <TextField
            label="Atende em outro hospital/clínica"
            value={attendances?.otherClinicsName || ''}
            disabled
            data-testid="input-external-health-insurances"
          />
          <TextField
            label="Vínculos com convênios externos"
            value={
              attendances?.externalHealthInsurances
                ? attendances.externalHealthInsurances.join(', ')
                : ''
            }
            required
            disabled
            data-testid="input-external-health-insurances"
          />
        </div>
      </S.AttendancesContainer>
      <S.PendencyJustification>
        <TextArea
          name={`${DoctorRegistrationSteps.ATTENDANCES}.description`}
          label="No caso de pendência, favor descrever"
          value={values[DoctorRegistrationSteps.ATTENDANCES].description}
          onChange={handleChange}
          data-testid="input-pendency-attendances"
          disabled={
            disableObservation(DoctorRegistrationSteps.ATTENDANCES) ||
            values[DoctorRegistrationSteps.ATTENDANCES].status !==
              DoctorRegisterStepStatus.PENDENT ||
            isStepsUpdateBlocked
          }
          required
        />
      </S.PendencyJustification>
      <S.ActionsButtons>
        <Button
          data-testid="btn-save-attendances"
          onClick={() => handleSubmit()}
          variant={isValid && !isStepsUpdateBlocked ? 'outlined' : undefined}
          disabled={!isValid || isStepsUpdateBlocked}
        >
          Salvar
        </Button>
      </S.ActionsButtons>
    </S.Wrapper>
  )
}

const otherLocationsAttendencesOptions = [
  {
    label: 'Sim',
    value: true
  },
  {
    label: 'Não',
    value: false
  }
]

const hasPrivateClinicOptions = [
  {
    label: 'Sim',
    value: true
  },
  {
    label: 'Não',
    value: false
  }
]

type registerPendencyValues = {
  [DoctorRegistrationSteps.ATTENDANCES]: {
    description?: string
    status: DoctorRegisterStepStatus
  }
}

export default Attendances
